import { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import type {
    ColumnDef,
    CustomField,
    FilterRule,
    TaskStatus,
    TaskPriority,
} from '@/lib/schemas';
import { useI18n } from '@/lib/i18n';
import { AddColumnRuleRow } from './add-column-dialog';

// ============================================
// EDIT COLUMN DIALOG
// ============================================
//
// Mirrors AddColumnDialog but pre-filled with the column's existing
// values. On save we hand the new column body to the supplied onSubmit
// callback — the parent (kanban-view) calls useUpdateColumn.mutate.

const PRESET_COLORS = [
    '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e',
    '#f97316', '#eab308', '#22c55e', '#14b8a6',
    '#06b6d4', '#3b82f6', '#64748b',
];

interface EditColumnDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    column: ColumnDef | null;
    onSubmit: (data: { name: string; color?: string; filterRules: FilterRule[] }) => Promise<void> | void;
    customFields: CustomField[];
    projectMembers?: Array<{ userId: string; user: { id: string; name: string } }>;
    isLoading?: boolean;
}

export function EditColumnDialog({
    open,
    onOpenChange,
    column,
    onSubmit,
    customFields,
    projectMembers = [],
    isLoading,
}: EditColumnDialogProps) {
    const { t } = useI18n();
    const [name, setName] = useState('');
    const [color, setColor] = useState<string | undefined>(undefined);
    const [rules, setRules] = useState<FilterRule[]>([]);

    // Reset form whenever the dialog opens on a different column.
    useEffect(() => {
        if (open && column) {
            setName(column.name);
            setColor(column.color);
            setRules(column.filterRules.length > 0 ? column.filterRules : [{ field: 'status', operator: 'eq', value: 'TODO' }]);
        }
    }, [open, column]);

    const updateRule = (idx: number, patch: Partial<FilterRule>) => {
        setRules((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    };

    const removeRule = (idx: number) => {
        setRules((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));
    };

    const addRule = () => {
        setRules((prev) => [...prev, { field: 'status', operator: 'eq', value: 'TODO' }]);
    };

    const handleSubmit = async () => {
        if (!name.trim() || rules.length === 0) return;
        await onSubmit({
            name: name.trim(),
            color,
            filterRules: rules,
        });
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{t('kanban.editColumnTitle')}</DialogTitle>
                    <DialogDescription>
                        {t('kanban.editColumnDescription')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div className="space-y-2">
                        <Label htmlFor="edit-col-name">{t('kanban.columnName')}</Label>
                        <Input
                            id="edit-col-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t('kanban.columnNamePlaceholder')}
                            autoFocus
                        />
                    </div>

                    <div className="space-y-2">
                        <Label>{t('kanban.columnColor')}</Label>
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                className={`h-7 w-7 rounded-full border ${!color ? 'ring-2 ring-primary' : 'border-white/20'}`}
                                style={{ background: 'linear-gradient(45deg, #6366f1, #ec4899)' }}
                                aria-label="No color"
                                onClick={() => setColor(undefined)}
                            />
                            {PRESET_COLORS.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    aria-label={`Color ${c}`}
                                    className={`h-7 w-7 rounded-full border ${color === c ? 'ring-2 ring-primary' : 'border-white/20'}`}
                                    style={{ backgroundColor: c }}
                                    onClick={() => setColor(c)}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <Label>{t('kanban.columnFilterRules')}</Label>
                            <Button variant="ghost" size="sm" onClick={addRule} type="button">
                                <Plus className="h-3.5 w-3.5 mr-1" />
                                {t('kanban.addRule')}
                            </Button>
                        </div>

                        <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                            {rules.map((rule, idx) => (
                                <AddColumnRuleRow
                                    key={idx}
                                    rule={rule}
                                    customFields={customFields}
                                    projectMembers={projectMembers}
                                    onChange={(patch) => updateRule(idx, patch)}
                                    onRemove={() => removeRule(idx)}
                                    canRemove={rules.length > 1}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isLoading || !name.trim() || rules.length === 0}
                    >
                        {t('common.saveChanges')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Re-export for callers that want to import the rule row directly.
export { PRESET_COLORS };
